import { ClientEnvironment } from '@polyai/common/constants/global.constants';
import Callout, { CalloutType } from '@polyai/ui/components/atoms/Callout';
import CopyBox from '@polyai/ui/components/atoms/CopyBox';
import Text from '@polyai/ui/components/atoms/Text';

import api from 'api';
import useProjectRoute from 'hooks/useProjectRoute';
import type { IntegrationFormConfig } from './types';

// Cluster-specific handoff_api host; set per environment in env_vars/.env.*.
const HANDOFF_API_BASE = (
  process.env.NEXT_PUBLIC_MESSAGING_HANDOFF_API_BASE ?? ''
).replace(/\/+$/, '');

// The webhook URL includes account/project so the handoff_api can look up the
// correct per-tenant webhook_secret from the secret service.
// env is hardcoded to "live" — the secret service doesn't have per-env webhook
// secrets yet, and "live" is used as the canonical env for all webhook traffic.
function buildWebhookUrl(accountId: string, projectId: string): string {
  return `${HANDOFF_API_BASE}/webhooks/zendesk/${accountId}/${projectId}/live/events`;
}

// Events we actually consume in handoff_api. Anything else adds noise; the
// provider only reacts to conversation:message, switchboard:passControl,
// and switchboard:offerControl, but we subscribe to the neighbouring
// delivery / typing / read events so Zendesk reports are complete.
const ZENDESK_WEBHOOK_SUBSCRIPTIONS = [
  'Switchboard offer control',
  'Switchboard pass control',
  'Conversation deleted',
  'Conversation message',
  'Conversation read',
  'User delivery success',
  'Delivery failure',
  'User typing',
];

function ZendeskSetupDocs() {
  const { accountId, projectId } = useProjectRoute();
  const webhookUrl = HANDOFF_API_BASE
    ? buildWebhookUrl(accountId, projectId)
    : null;

  return (
    <div className="flex flex-col gap-sm2">
      {!webhookUrl && (
        <Callout type={CalloutType.WARNING}>
          <Text>
            Webhook URL unavailable —{' '}
            <span className="font-mono">
              NEXT_PUBLIC_MESSAGING_HANDOFF_API_BASE
            </span>{' '}
            is not configured for this environment.
          </Text>
        </Callout>
      )}
      <Callout type={CalloutType.NEUTRAL} hideIcon>
        <div className="flex flex-col gap-xs2">
          <Text variant="headerSmall">Create a Conversations integration</Text>
          <ol className="list-decimal pl-sm3 flex flex-col gap-xs3">
            <li>
              <Text>
                Go to{' '}
                <span className="font-semibold">
                  Admin → Apps and integrations → Conversations integrations
                </span>
              </Text>
            </li>
            <li>
              <Text>
                Click <span className="font-semibold">Create integration</span>
              </Text>
            </li>
            <li>
              <Text>
                Set the{' '}
                <span className="font-semibold">Webhook endpoint URL</span> to:
              </Text>
              {webhookUrl && (
                <div className="mt-xs3 [&_p]:break-all">
                  <CopyBox copyText={webhookUrl} />
                </div>
              )}
            </li>
            <li>
              <Text>
                Under <span className="font-semibold">Webhook body data</span>,
                check <span className="font-mono">Include full source</span>
              </Text>
            </li>
            <li>
              <Text>
                Under{' '}
                <span className="font-semibold">Webhook subscriptions</span>,
                check the following events:
              </Text>
              <ul className="list-disc pl-sm3 mt-xs3 flex flex-col gap-xs3">
                {ZENDESK_WEBHOOK_SUBSCRIPTIONS.map(event => (
                  <li key={event}>
                    <Text>
                      <span className="font-mono">{event}</span>
                    </Text>
                  </li>
                ))}
              </ul>
            </li>
            <li>
              <Text>
                Click <span className="font-semibold">Save</span>. In the
                confirmation dialog, copy the{' '}
                <span className="font-semibold">Shared secret</span> — you will
                need it in the next step.
              </Text>
            </li>
            <li>
              <Text>
                Once the dialog closes, note the{' '}
                <span className="font-semibold">Integration ID</span> shown on
                the integration page.
              </Text>
            </li>
            <li>
              <Text>
                Open the <span className="font-semibold">API Keys</span> tab and
                click <span className="font-semibold">Create API key</span>.
                Note the <span className="font-semibold">App ID</span>,{' '}
                <span className="font-semibold">Key ID</span>, and{' '}
                <span className="font-semibold">Secret key</span>.
              </Text>
            </li>
          </ol>
        </div>
      </Callout>
    </div>
  );
}

export const zendeskMessagingHandoffFormConfig: IntegrationFormConfig = {
  onDisconnect: async (accountId, projectId, _recordId, queryClient) => {
    await api.deleteAllMessagingHandoffSecrets({
      accountId,
      projectId,
      provider: 'zendesk',
    });
    await queryClient?.invalidateQueries({
      queryKey: [accountId, projectId, 'messaging-handoff-secret', 'zendesk'],
    });
  },
  steps: [
    {
      title: 'Zendesk Messaging setup',
      description:
        'Create a Conversations integration in Zendesk and collect the credentials below.',
      docs: <ZendeskSetupDocs />,
      schema: {
        type: 'object',
        properties: {},
      },
      uiSchema: {},
    },
    {
      title: 'Zendesk Messaging credentials',
      description:
        'Enter the credentials from the previous step. They never leave our infrastructure.',
      schema: {
        type: 'object',
        properties: {
          app_id: {
            type: 'string',
            title: 'App ID',
          },
          integration_id: {
            type: 'string',
            title: 'Integration ID',
          },
          api_url: {
            type: 'string',
            title: 'API base URL',
            pattern: '^https://.*',
          },
          target_switchboard_integration: {
            type: 'string',
            title: 'Target switchboard integration',
            default: 'next',
          },
          key_id: {
            type: 'string',
            title: 'Key ID',
          },
          key_secret: {
            type: 'string',
            title: 'Secret key',
            format: 'password',
          },
          webhook_secret: {
            type: 'string',
            title: 'Webhook shared secret',
            format: 'password',
          },
        },
        required: [
          'app_id',
          'integration_id',
          'api_url',
          'key_id',
          'key_secret',
          'webhook_secret',
        ],
      },
      uiSchema: {
        api_url: {
          'ui:patternErrorMessage': 'API URL must use HTTPS',
          'ui:placeholder': 'https://{subdomain}.zendesk.com',
        },
        target_switchboard_integration: {
          'ui:placeholder': 'next',
        },
      },
      connectButtonLabel: 'Save credentials',
      onSubmit: async (accountId, projectId, data) => {
        const {
          app_id,
          integration_id,
          api_url,
          target_switchboard_integration,
          key_id,
          key_secret,
          webhook_secret,
        } = data;
        const secretData = {
          app_id,
          integration_id,
          api_url,
          target_switchboard_integration:
            target_switchboard_integration || 'next',
          key_id,
          key_secret,
          webhook_secret,
        };
        // Serial: backend PUT is read-modify-write per env; parallel writes
        // race and can drop envs that were just added.
        for (const clientEnv of Object.values(ClientEnvironment)) {
          await api.upsertMessagingHandoffSecret({
            accountId,
            projectId,
            provider: 'zendesk',
            clientEnv,
            data: secretData,
          });
        }
      },
    },
  ],
};

export default zendeskMessagingHandoffFormConfig;
